import { Lesson, LessonPart, CoursePhase } from '@/models/course';
import { MediaTypes } from '@/constants';
import { MaterialFormOption, MaterialTextField } from '@/shared/components/form/MaterialFormElements';
import DropZoneField from '@/shared/components/form/dropzones/DropZone';
import { addButtonStyle, dangerButtonStyle, darkDangerText, iconButtonStyle} from '@/shared/dark-inline-styles';
const uuidv4 = () => crypto.randomUUID();
import ArrowUpIcon from 'mdi-react/ArrowUpIcon';
import ArrowDownIcon from 'mdi-react/ArrowDownIcon';
import DeleteIcon from 'mdi-react/DeleteIcon';
import PlusIcon from 'mdi-react/PlusIcon';

interface LessonsEditorProps {
    lessons: Lesson[];
    phases: CoursePhase[];
    // Functional updater (not a plain array): the media DropZone's onChange is
    // async (fires seconds later, when the chunked upload finishes). If updates
    // computed against the `lessons` prop snapshot, that late write would clobber
    // fields typed in the meantime (or be clobbered itself). Merging into the
    // LATEST state via a function makes every update race-free.
    onChange: (updater: (prev: Lesson[]) => Lesson[]) => void;
    // Forwarded to every part's media DropZone so the parent can block "Save"
    // while any lesson media is still uploading.
    onUploadingChange?: (uploading: boolean) => void;
}

const emptyPart = (): LessonPart => ({ title: '', description: '', durationSeconds: 0, mediaPath: '', mediaType: MediaTypes.Audio });

// A lesson's parts, tolerant of legacy single-section docs (flat title/media).
const lessonPartsForEdit = (l: Lesson): LessonPart[] => {
    if (l.parts?.length) return l.parts;
    if (l.mediaPath || l.title) {
        return [
            {
                title: l.title ?? '',
                description: l.description ?? '',
                durationSeconds: l.durationSeconds ?? 0,
                mediaPath: l.mediaPath ?? '',
                mediaType: l.mediaType ?? MediaTypes.Audio,
                mediaLink: l.mediaLink,
            },
        ];
    }
    return [];
};

const LessonsEditor = ({ lessons, phases, onChange, onUploadingChange }: LessonsEditorProps) => {
    // Mirror part[0] onto the legacy single-section fields so older clients
    // (mobile) keep showing part 1.
    const withMirroredPrimary = (lesson: Lesson, parts: LessonPart[]): Lesson => {
        const primary = parts[0];
        return {
            ...lesson,
            parts,
            title: primary?.title ?? '',
            description: primary?.description ?? '',
            durationSeconds: primary?.durationSeconds ?? 0,
            mediaPath: primary?.mediaPath ?? '',
            mediaType: primary?.mediaType ?? MediaTypes.Audio,
        };
    };

    const updateLesson = (index: number, patch: Partial<Lesson>) => {
        onChange((prev) => prev.map((l, i) => (i === index ? { ...l, ...patch } : l)));
    };

    // Patch one part of a lesson, computed against the LATEST state.
    const updatePart = (lessonIdx: number, partIdx: 0 | 1, patch: Partial<LessonPart>) => {
        onChange((prev) =>
            prev.map((l, i) => {
                if (i !== lessonIdx) return l;
                const current = [...lessonPartsForEdit(l)];
                while (current.length <= partIdx) current.push(emptyPart());
                current[partIdx] = { ...current[partIdx], ...patch };
                return withMirroredPrimary(l, current);
            }),
        );
    };

    // DropZone hands back a MediaTypes value (it already maps the MIME), but we
    // normalize defensively.
    const setPartMedia = (lessonIdx: number, partIdx: 0 | 1, mediaPath: string, mediaType: MediaTypes) => {
        const type = (String(mediaType).split('/')[0] as MediaTypes) || MediaTypes.Audio;
        updatePart(lessonIdx, partIdx, { mediaPath, mediaType: type });
    };

    // Drop a part entirely (used for the optional second part). part[0] stays
    // required, so this only removes trailing parts and re-mirrors the primary.
    const removePart = (lessonIdx: number, partIdx: number) => {
        onChange((prev) => prev.map((l, i) => (i === lessonIdx ? withMirroredPrimary(l, lessonPartsForEdit(l).filter((_, j) => j !== partIdx)) : l)));
    };

    // True once the admin has put anything into a part — used to show the
    // "Remove" control on the optional second part.
    const partHasContent = (p: LessonPart): boolean => !!(p.title?.trim() || p.description?.trim() || p.mediaPath?.trim() || p.durationSeconds);

    const addLesson = () => {
        onChange((prev) => [...prev, { id: uuidv4(), order: prev.length, parts: [emptyPart()] }]);
    };

    const removeLesson = (index: number) => {
        onChange((prev) => prev.filter((_, i) => i !== index).map((l, i) => ({ ...l, order: i })));
    };

    const moveLesson = (index: number, direction: -1 | 1) => {
        const target = index + direction;
        onChange((prev) => {
            if (target < 0 || target >= prev.length) return prev;
            const next = [...prev];
            [next[index], next[target]] = [next[target], next[index]];
            return next.map((l, i) => ({ ...l, order: i }));
        });
    };

    const renderPartEditor = (lessonIdx: number, partIdx: 0 | 1, label: string) => {
        const part = lessonPartsForEdit(lessons[lessonIdx])[partIdx] ?? emptyPart();
        // The optional second part can be removed once it has any content.
        const showRemove = partIdx === 1 && partHasContent(part);
        return (
            <fieldset style={{ border: '1px solid #d8d8d8', borderRadius: 8, padding: 10, marginTop: 10 }}>
                <legend style={{ fontWeight: 600, fontSize: 13, padding: '0 6px', display: 'flex', alignItems: 'center', gap: 8 }}>
                    {label}
                    {showRemove && (
                        <button
                            type="button"
                            onClick={() => removePart(lessonIdx, partIdx)}
                            title="Remove this part"
                            style={{ ...dangerButtonStyle, fontSize: 12 }}
                        >
                            <DeleteIcon size={14} /> Remove
                        </button>
                    )}
                </legend>
                <MaterialTextField label="Section title" value={part.title} onChange={(e) => updatePart(lessonIdx, partIdx, { title: e.target.value })} />
                <MaterialTextField
                    label="Section description"
                    multiline
                    value={part.description}
                    onChange={(e) => updatePart(lessonIdx, partIdx, { description: e.target.value })}
                />
                <MaterialTextField
                    label="Duration (seconds)"
                    type="number"
                    value={part.durationSeconds || ''}
                    onChange={(e) => updatePart(lessonIdx, partIdx, { durationSeconds: Number(e.target.value) })}
                />
                <div style={{ marginTop: 8 }}>
                    <p style={{ marginBottom: 6 }}>Media (audio or video)</p>
                    <DropZoneField
                        customHeight
                        accept="audio/mp3, audio/mpeg, video/mp4"
                        currentValue={part.mediaLink || part.mediaPath || ''}
                        currentFileType={part.mediaType || 'audio'}
                        onChange={(mediaPath, mediaType) => setPartMedia(lessonIdx, partIdx, mediaPath, mediaType)}
                        onUploadingChange={onUploadingChange}
                        description={part.description}
                    />
                </div>
            </fieldset>
        );
    };

    return (
        <div>
            <p style={{ fontWeight: 600, marginBottom: 8 }}>Lessons ({lessons.length})</p>
            {lessons.length === 0 && (
                <p style={{ color: darkDangerText, fontSize: 13 }}>A course must have at least one lesson with a completed first part before saving.</p>
            )}
            {lessons.map((lesson, idx) => (
                <div
                    key={lesson.id}
                    style={{
                        border: '1px solid #e0e0e0',
                        borderRadius: 8,
                        padding: 12,
                        marginBottom: 10,
                        background: 'rgba(0,0,0,0.02)',
                    }}
                >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                        <strong>Lesson {idx + 1}</strong>
                        <div style={{ display: 'flex', gap: 6 }}>
                            <button type="button" onClick={() => moveLesson(idx, -1)} disabled={idx === 0} title="Move up" style={iconButtonStyle}>
                                <ArrowUpIcon size={16} />
                            </button>
                            <button
                                type="button"
                                onClick={() => moveLesson(idx, 1)}
                                disabled={idx === lessons.length - 1}
                                title="Move down"
                                style={iconButtonStyle}
                            >
                                <ArrowDownIcon size={16} />
                            </button>
                            <button type="button" onClick={() => removeLesson(idx)} title="Delete lesson" style={dangerButtonStyle}>
                                <DeleteIcon size={16} />
                            </button>
                        </div>
                    </div>

                    {phases.length > 0 && (
                        <MaterialTextField
                            name="select"
                            select
                            label="Phase"
                            value={lesson.phaseId ?? ''}
                            onChange={(e) => updateLesson(idx, { phaseId: e.target.value || undefined })}
                        >
                            <MaterialFormOption value="">— None —</MaterialFormOption>
                            {phases.map((p) => (
                                <MaterialFormOption key={p.id} value={p.id}>
                                    {p.title || `Phase ${p.order + 1}`}
                                </MaterialFormOption>
                            ))}
                        </MaterialTextField>
                    )}

                    {renderPartEditor(idx, 0, 'Part 1 (required)')}
                    {renderPartEditor(idx, 1, 'Part 2 (optional)')}
                </div>
            ))}
            <button type="button" onClick={addLesson} style={addButtonStyle}>
                <PlusIcon size={16} /> Add Lesson
            </button>
        </div>
    );
};

export default LessonsEditor;
